/* eslint-disable no-magic-numbers, @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { GtmService } from '@shared/service.gtm';

@Injectable({
  providedIn: 'root'
})
export class GtmTrackingService {
  private readonly gtmService = inject(GtmService);

  /**
   * Track page load events for promo pages
   */
  trackPageLoad(pageName: string, promoType?: string): void {
    const label = promoType ? `${pageName} - ${promoType}` : pageName;
    this.gtmService.sendBasicEvent(
      'page_load',
      'Promo',
      'Load Page',
      label
    );
  }

  /**
   * Track button clicks with promo context
   */
  trackButtonClick(buttonName: string, buttonContext: string = 'Promo'): void {
    this.gtmService.eventClickButton(
      'button_click',
      'Promo',
      buttonContext,
      buttonName
    );
  }

  /**
   * Track promo view events with enhanced e-commerce data
   */
  trackPromoView(promoData: {
    id: string;
    name: string;
    category?: string;
    type?: string;
  }): void {
    this.gtmService.pushEvent({
      event: 'view_item',
      event_category: 'promo',
      ecommerce: {
        currency: 'IDR',
        value: 0, // Promo has no monetary value
        items: [{
          item_id: promoData.id,
          item_name: promoData.name,
          item_category: promoData.category || 'promo',
          item_category2: promoData.type || 'unknown',
          affiliation: 'Telkomsel',
          quantity: 1
        }]
      }
    });
  }

  /**
   * Track package/offer list view events
   */
  trackPackageListView(packages: any[], listId: string, listName: string, promoType?: string): void {
    if (packages && packages.length > 0) {
      const items = packages.map((pkg, index) => ({
        item_id: pkg.id || `package_${index}`,
        item_name: pkg.name || pkg.package_name || 'Unknown Package',
        item_category: 'promo_package',
        item_category2: promoType || 'promo',
        affiliation: 'Telkomsel',
        currency: 'IDR',
        price: pkg.price || pkg.package_price || 0,
        quantity: 1,
        index: index + 1
      }));

      this.gtmService.pushEvent({
        event: 'view_item_list',
        event_category: 'ecommerce',
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items: items
        }
      });
    }
  }

  /**
   * Track package/offer selection
   */
  trackPackageSelection(packageData: any, promoType?: string): void {
    this.gtmService.pushEvent({
      event: 'select_item',
      event_category: 'ecommerce',
      ecommerce: {
        item_list_id: 'promo_packages',
        item_list_name: `${promoType || 'Promo'} Packages`,
        currency: 'IDR',
        items: [{
          item_id: packageData.id || 'unknown',
          item_name: packageData.name || packageData.package_name || 'Unknown Package',
          item_category: 'promo_package',
          item_category2: promoType || 'promo',
          affiliation: 'Telkomsel',
          currency: 'IDR',
          price: packageData.price || packageData.package_price || 0,
          quantity: 1
        }]
      }
    });
  }

  /**
   * Track purchase intent (begin checkout)
   */
  trackPurchaseIntent(packageData: any, promoType?: string): void {
    this.gtmService.pushEvent({
      event: 'begin_checkout',
      event_category: 'ecommerce',
      ecommerce: {
        currency: 'IDR',
        value: packageData.price || packageData.package_price || 0,
        items: [{
          item_id: packageData.id || 'unknown',
          item_name: packageData.name || packageData.package_name || 'Unknown Package',
          item_category: 'promo_package',
          item_category2: promoType || 'promo',
          affiliation: 'Telkomsel',
          currency: 'IDR',
          price: packageData.price || packageData.package_price || 0,
          quantity: 1
        }]
      }
    });
  }

  /**
   * Track user interactions with promo content
   */
  trackPromoInteraction(action: string, label?: string): void {
    this.gtmService.sendBasicEvent(
      action,
      'Promo',
      'User Interaction',
      label || ''
    );
  }

  /**
   * Track authentication related events (OTP, login)
   */
  trackAuthEvent(action: 'otp_request' | 'otp_submit' | 'otp_resend' | 'login', context?: string): void {
    this.gtmService.sendBasicEvent(
      action,
      'Promo',
      'Authentication',
      context || action.replace('_', ' ').toUpperCase()
    );
  }

  /**
   * Track error events
   */
  trackError(errorMessage: string, context?: string): void {
    this.gtmService.sendBasicEvent(
      'error',
      'Promo',
      context || 'Error',
      errorMessage
    );
  }
}
